import { useLayoutEffect, useState } from "react";

export type CustomBreakpointProps = Record<string, number>;

export type RegisteredMediaQuery = {
  width: number;
  mediaQuery: MediaQueryList;
  handler: EventListenerOrEventListenerObject;
};

const getBreakpoints = (width: number, breakpointConfig) => {
  return Object.keys(breakpointConfig).reduce((accumulator, currentValue) => {
    accumulator[currentValue] = breakpointConfig[currentValue] <= width;
    return accumulator;
  }, {});
};

export const useCustomBreakpointHook = (
  customBreakpointProps: CustomBreakpointProps
): Record<string, boolean> => {
  const [breakpoints, setBreakpoints] = useState(
    getBreakpoints(window?.innerWidth || 0, customBreakpointProps)
  );

  useLayoutEffect(() => {
    const handleEventChange = () => {
      const newBreakpoint = getBreakpoints(
        window?.innerWidth || 0,
        customBreakpointProps
      );
      setBreakpoints(newBreakpoint);
    };

    const registeredMediaQueryList: RegisteredMediaQuery[] = window
      ? Object.keys(customBreakpointProps).map((breakpointKey) => {
          const widthConfig = customBreakpointProps[breakpointKey];

          const mediaQuery = window.matchMedia(`(min-width: ${widthConfig}px)`);
          mediaQuery.addEventListener("change", handleEventChange);

          return {
            width: customBreakpointProps[breakpointKey],
            mediaQuery,
            handler: handleEventChange,
          };
        })
      : [];

    return () => {
      registeredMediaQueryList.forEach((registeredMediaQuery) => {
        registeredMediaQuery.mediaQuery.removeEventListener(
          "change",
          registeredMediaQuery.handler
        );
      });
    };
  }, [customBreakpointProps]);

  return breakpoints;
};
